// useGanttData — adapts the existing React-Query production data layer into the
// board's view model (lanes / entries / families / downtime) and exposes a
// schedule-family mutation with optimistic backlog removal.
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listScheduleAll, listPressConfig, listDowntimeWindows, listJobs, addScheduleEntry,
  deleteScheduleEntry, updateScheduleEntry, ScheduleCreate, ScheduleUpdate,
  createDowntimeWindow, updateDowntimeWindow, deleteDowntimeWindow, DowntimeWindowUpsert,
  splitJobs, rejoinSplit, rejoinJobs, rebaseSchedule, clearFutureSchedule,
} from '../productionApi';
import { productionKeys } from '../queryKeys';
import { ScheduleEntry, ProductionJob, DowntimeWindow } from '../productionTypes';
import { materialiseDowntimeWindows, DowntimeInterval, splitRunAroundDowntime } from '../productionUtils';
import { barStyle, ColourMap } from './barStyle';
import { useCustomerColours } from './useCustomerColours';
import { GanttEntry, GanttLane, GanttFamily, GanttSep } from './ganttTypes';
import { useArtworkSeparations, ArtworkRef, ArtworkResolution } from './useArtworkSeparations';
import {
  PARK_CODE, ELSEWHERE_CODE, SHEETS_PER_MIN, DEFAULT_SETUP_MIN, DEFAULT_WASHUP_MIN, pressRank,
  MIN_HORIZON_MIN, HORIZON_BUFFER_MIN, LIVE_REFRESH_MS, hasCoating,
} from './ganttConfig';

const MS_DAY = 86_400_000;
const MS_MIN = 60_000;

function todayMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// NO PLACEHOLDER INK STRIP. There used to be a hardcoded B/C/M/Y fallback here,
// shown while the artwork was still loading and on backlog/parked tiles that are
// never resolved at all. It was indistinguishable from a real four-colour job, so
// the board asserted a separation set it had never read — worst on the hover card
// and the printed run sheet, which name and badge the inks. An unresolved artwork
// now yields an EMPTY strip and callers show "reading…" / nothing. Absence of
// information beats confident wrong information on a press schedule.

function dueOffset(due: string | null | undefined, base: Date): number | null {
  if (!due) return null;
  const d = new Date(due);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - base.getTime()) / MS_DAY);
}

function adaptEntry(
  e: ScheduleEntry, base: Date, nowAbs: number, colourMap: ColourMap,
  resByRef: Map<string, ArtworkResolution>, windows: DowntimeWindow[],
): GanttEntry {
  // Resolve this bar's artwork inks/status (scheduled bars only; keyed by the
  // finished-good item code). Only a RESOLVED artwork produces a strip: absent
  // (still queued in the concurrency-1 fetch) and not_found/error/no_path all
  // yield []. Consumers read artworkStatus to tell those apart — undefined means
  // still loading, and they say so rather than drawing inks we haven't read.
  //
  // A SECOND PASS PRINTS THE REVERSE SIDE, and we hold no record of the reverse
  // side's artwork anywhere — not in Prism, not by any Cloudflow convention, and
  // prepress.artwork_record is one row per item code with no notion of a side.
  // The resolution above is the FRONT's, so on a pass-2 bar it is not just
  // incomplete, it is the wrong side's colours. Blanked here, at the one place
  // every consumer reads from, so the bar strip, the hover and the unit-count
  // check are all honestly empty rather than confidently wrong. The run sheet
  // additionally says WHY (see PressRunSheetDialog).
  const res = e.customer_ref ? resByRef.get(e.customer_ref.trim()) : undefined;
  const backSide = (e.pass_no ?? 1) > 1;
  const seps: GanttSep[] = res?.status === 'resolved' && !backSide ? res.seps : [];
  const toAbs = (iso: string | null | undefined): number | null =>
    iso ? (new Date(iso).getTime() - base.getTime()) / MS_MIN : null;

  // Committed plan (the faint reference) and its duration.
  const planStart = (new Date(e.planned_start).getTime() - base.getTime()) / MS_MIN;
  const plannedDur =
    (e.planned_setup_min || 0) + (e.planned_run_min || 0) + (e.planned_washup_min || 0);
  const planEnd = planStart + plannedDur;

  // LIVE positions: where the job will really run (projection), falling back to
  // the committed plan when there's no projection. The solid bar is drawn here,
  // so the NOW line always sits on the job actually running.
  const projS = toAbs(e.projected_start);
  const projE = toAbs(e.projected_end);
  const absStart = projS ?? planStart;

  // Split the run around closed downtime bands (weekend / plant closed), so a
  // run that crosses a closure pauses and resumes on the other side rather than
  // drawing straight through it. effectiveEnd includes the pause, so the bar's
  // footprint (and the next job's start) reflect real wall-clock. Completed
  // bars keep their real actual span — no re-splitting.
  const bMs = base.getTime();
  let segments: { startMin: number; endMin: number }[];
  let absEnd: number;
  if (e.status === 'completed') {
    absEnd = projE ?? planEnd;
    segments = [{ startMin: absStart, endMin: absEnd }];
  } else if (e.status === 'in_progress' && projE != null) {
    // Running bar spans its REAL start to the LIVE projected finish (the ETA
    // from cumulative progress at standard rate), NOT the static planned
    // duration — so it shrinks when the press is ahead and grows when it drags,
    // and sits flush against the next job (which the backend cascades from this
    // same projected end). projE already accounts for downtime; a running job
    // crossing a closure is rare enough not to re-split here.
    absEnd = projE;
    segments = [{ startMin: absStart, endMin: absEnd }];
  } else {
    // Downtime is PER PRESS — only this bar's OWN lane may pause it. Passing the
    // whole rule set made another press's closure stretch this bar: a P15 job
    // would get pushed out of a P11 maintenance block, then out of the weekend
    // band behind it, landing days later. GanttBoard paints bands filtered by
    // press, so nothing on screen explained the stretch — the bar just grew and
    // overlapped its neighbours, stacking the lane into extra sub-rows.
    const laneWindows = windows.filter((w) => w.press_code === e.press_code);
    const split = splitRunAroundDowntime(
      new Date(bMs + absStart * MS_MIN).toISOString(),
      plannedDur,
      laneWindows,
      {
        runThrough: e.run_through_downtime ?? false,
        remakeReadyMinutes: e.needs_remakeready_after_pause ? (e.planned_setup_min || 0) : 0,
      },
    );
    segments = split.segments.map((s) => ({
      startMin: (s.start.getTime() - bMs) / MS_MIN,
      endMin: (s.end.getTime() - bMs) / MS_MIN,
    }));
    absEnd = (split.effectiveEnd.getTime() - bMs) / MS_MIN;
  }
  const planned_total = absEnd - absStart; // VIEW span — includes any downtime pause
  const day = Math.floor(absStart / 1440);
  const start = absStart - day * 1440;

  // How far through the run — by GOOD sheets produced vs the order quantity.
  // Deliberately NOT time-elapsed: a stalled press shouldn't creep to 100%.
  // Computed for running AND completed bars so a finished job's hover shows the
  // same "good / ordered · %" yield the operator watched while it ran.
  const sheetsDone = e.quantity_good ?? null;
  let progress: number | null = null;
  if ((e.status === 'in_progress' || e.status === 'completed') && e.run_length_sheets && sheetsDone != null) {
    progress = Math.min(1, Math.max(0, sheetsDone / e.run_length_sheets));
  }

  return {
    id: e.id,
    job: e.job_number,
    cust: e.customer_code ?? '',
    custName: e.customer_name ?? e.customer_code ?? '',
    ref: e.customer_ref ?? null,
    press: e.press_code,
    forme: e.forme_number ?? null,
    stockCode: e.stock_code ?? null,
    materialDesc: e.material_desc ?? null,
    coating: e.coating ?? null,
    length: e.length ?? null,
    width: e.width ?? null,
    desc: e.description ?? '',
    sheets: e.run_length_sheets ?? 0,
    status: e.status,
    pass: e.pass_no ?? 1,
    passCount: e.pass_count ?? 1,
    familyRunId: e.family_run_id ?? null,
    sequence: e.sequence,
    setupMin: e.planned_setup_min || 0,
    runMin: e.planned_run_min || 0,
    washupMin: e.planned_washup_min || 0,
    day, start, absStart, absEnd, planned_total,
    planStart, planEnd,
    due: dueOffset(e.due_date, base),
    progress, sheetsDone,
    projStart: projS,
    projEnd: projE,
    actualUpdatedAt: e.actual_updated_at ?? null,
    machineStatus: e.machine_status ?? null,
    machineActivity: e.machine_activity ?? null,
    liveSpeed: e.live_speed ?? null,
    operator: e.operator ?? null,
    style: barStyle(e.customer_code, e.forme_number, colourMap),
    seps,
    segments,
    artworkStatus: res?.status,
    artworkPath: res?.pdfPath ?? null,
    artworkError: res?.error ?? null,
  };
}

// Union a family's resolved artworks into one card-level ink strip. A gang
// family can hold several artworks; what matters for planning is what has to
// be on the press, so we dedupe by ink NAME across the family and order
// process → spot → coating (spots are the decision-relevant part; process is
// a familiar compact prefix). Counts drive the "read X of Y" indicator.
function unionFamilySeps(
  refs: (string | null | undefined)[],
  resByRef: Map<string, ArtworkResolution>,
): { seps: GanttSep[]; artworkCount: number; resolvedArtworks: number } {
  const distinct = new Set(refs.map((r) => (r || '').trim()).filter(Boolean));
  const byName = new Map<string, GanttSep>();
  let resolved = 0;
  distinct.forEach((ref) => {
    const res = resByRef.get(ref);
    if (res?.status !== 'resolved') return;
    resolved += 1;
    for (const s of res.seps) {
      const key = (s.name || s.short).toLowerCase();
      if (!byName.has(key)) byName.set(key, s);
    }
  });
  const rank = (s: GanttSep) => (s.role === 'process' ? 0 : s.role === 'coating' ? 2 : 1);
  const seps = Array.from(byName.values()).sort((a, b) => rank(a) - rank(b));
  return { seps, artworkCount: distinct.size, resolvedArtworks: resolved };
}

/**
 * The structural part of a family key: the forme, or — for a job with no forme —
 * its trim size. Label work (UBA, MFA, FMA…) carries no die/forme in Prism; its
 * gang runs are the jobs that share a trim, so without this every forme-less job
 * of a customer collapsed into one family. Prefixed so a trim can never collide
 * with a forme number. Mirrors _structural_key in blueprints/production.py — the
 * job-groups endpoint must group the same way, keep the two in step.
 */
function structuralKey(
  forme: string | null | undefined, length: number | null | undefined, width: number | null | undefined,
): string {
  if (forme) return forme;
  if (length == null || width == null) return '_';
  return `trim:${Number(length)}x${Number(width)}`;
}

function buildFamilies(
  jobs: ProductionJob[], base: Date, resByRef: Map<string, ArtworkResolution>,
): GanttFamily[] {
  // Group by (customer, forme, stock_code) AND split_key, so a manually
  // split-off subset forms its own card rather than re-merging with its forme
  // siblings.
  //
  // stock_code is part of the key because a card is labelled from its head job
  // (earliest due) — its material, its Paper/Cartons tab, its Paper-tab section
  // header. On a mixed-material card every other job inherits a material that
  // isn't theirs. One forme on two stocks was never one press run anyway: it's
  // a different pallet and a different sheet size in the feeder.
  //
  // coating too, for the same head-job-labels-the-card reason: a varnish job
  // grouped under a gloss head (or vice versa) reads as the wrong coater setup
  // before it's ever dragged onto a press. 'NONE' and blank both mean uncoated,
  // so they normalise to one bucket rather than splitting identical cards.
  //
  // ...AND by pass. The backlog arrives one row per (job, pass) still needing a
  // slot, so a two-pass forme yields a "Pass 1" card and a "Pass 2" card. That's
  // deliberate and matches how the shop runs them: all the fronts for a range of
  // jobs go down together, the backs follow later and independently. Scheduling
  // the Pass 1 card leaves Pass 2 sitting in the dock.
  const map = new Map<string, ProductionJob[]>();
  for (const j of jobs) {
    const split = j.split_key ?? '';
    const pass = j.pass_no ?? 1;
    const coat = hasCoating(j.coating) ? j.coating : '_';
    const struct = structuralKey(j.forme_number, j.length, j.width);
    const key = `${j.customer_code ?? '_'}|${struct}|${j.stock_code ?? '_'}|${coat}|${split}|p${pass}`;
    const arr = map.get(key);
    if (arr) arr.push(j);
    else map.set(key, [j]);
  }
  const fams: GanttFamily[] = [];
  map.forEach((arr, key) => {
    arr.sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''));
    const head = arr[0];
    const run = arr.reduce((s, j) => s + Math.round((j.run_length_sheets ?? 0) / SHEETS_PER_MIN), 0);
    // A Pass 2 card prints the REVERSE side, and we hold no record of the
    // reverse side's artwork anywhere — same reasoning as the pass-2 blanking
    // in adaptEntry. The front's inks would be the wrong side's colours, so
    // the card carries none (resolvedArtworks: 0 keeps the indicator honest).
    const union = (head?.pass_no ?? 1) > 1
      ? { seps: [], artworkCount: 0, resolvedArtworks: 0 }
      : unionFamilySeps(arr.map((j) => j.customer_ref), resByRef);
    fams.push({
      famId: key,
      forme: head?.forme_number ?? null,
      cust: head?.customer_code ?? null,
      custName: head?.customer_name ?? head?.customer_code ?? '',
      stockCode: head?.stock_code ?? null,
      materialDesc: head?.material_desc ?? null,
      coating: head?.coating ?? null,
      length: head?.length ?? null,
      width: head?.width ?? null,
      jobs: arr.map((j) => ({
        job: j.job_number, desc: j.description ?? '',
        sheets: j.run_length_sheets ?? 0, due: dueOffset(j.due_date, base),
        pass: j.pass_no ?? 1,
        ref: j.customer_ref ?? null,
      })),
      // Card ink strip: union of the family's CACHED artwork inks (bulk-only —
      // the pool never trickles Cloudflow). Unresolved artworks contribute
      // nothing; the card shows "read X of Y" rather than a guessed strip.
      ...union,
      setup: DEFAULT_SETUP_MIN, run, washup: DEFAULT_WASHUP_MIN,
      earliestDue: dueOffset(head?.due_date, base),
      units: 4,
      splitKey: head?.split_key ?? null,
      pass: head?.pass_no ?? 1,
      twoPass: (head?.pass_count ?? 1) > 1,
    });
  });
  fams.sort((a, b) => (a.earliestDue ?? 9999) - (b.earliestDue ?? 9999));
  return fams;
}

// Group "held" schedule rows (Parking or Scheduled-elsewhere) into the same
// family tiles the dock/Parking lane render as draggable chips. Unlike backlog
// families these already carry resolved durations and a schedule id, so moving
// one to a press deletes its source row (handled in scheduleFamily). `parked`
// is set only for true Parking families — it drives the re-park-is-a-no-op
// guard in the drag handler; elsewhere families leave it false.
function buildHeldFamilies(
  rows: ScheduleEntry[], base: Date, famPrefix: string, parked: boolean,
  resByRef: Map<string, ArtworkResolution>,
): GanttFamily[] {
  const map = new Map<string, ScheduleEntry[]>();
  for (const r of rows) {
    // A row that was on a press before being held carries its family run, and
    // THAT is the chip: two runs of one forme parked together stay two chips,
    // and each goes back to the lane with the id it left with. Rows that never
    // reached a press have no run yet and fall back to the derived key. Pass is
    // part of the derived key for the same reason it is in buildFamilies: two
    // passes of a forme parked together are two separate runs, and merging them
    // into one chip would make "return to backlog" ambiguous about which it meant.
    const key = r.family_run_id != null
      ? `fr:${r.family_run_id}`
      : `${r.customer_code ?? '_'}|${structuralKey(r.forme_number, r.length, r.width)}|p${r.pass_no ?? 1}`;
    const arr = map.get(key);
    if (arr) arr.push(r);
    else map.set(key, [r]);
  }
  const fams: GanttFamily[] = [];
  map.forEach((arr, key) => {
    arr.sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''));
    const head = arr[0];
    const sum = (f: (e: ScheduleEntry) => number) => arr.reduce((s, e) => s + f(e), 0);
    fams.push({
      famId: `${famPrefix}:${key}`,
      forme: head?.forme_number ?? null,
      cust: head?.customer_code ?? null,
      custName: head?.customer_name ?? head?.customer_code ?? '',
      stockCode: head?.stock_code ?? null,
      materialDesc: head?.material_desc ?? null,
      coating: head?.coating ?? null,
      length: head?.length ?? null,
      width: head?.width ?? null,
      jobs: arr.map((e) => ({
        job: e.job_number, desc: e.description ?? '',
        sheets: e.run_length_sheets ?? 0, due: dueOffset(e.due_date, base),
        scheduleId: e.id,
        pass: e.pass_no ?? 1,
        ref: e.customer_ref ?? null,
      })),
      // Parked / scheduled-elsewhere chips: same union-of-cached-artworks strip
      // as backlog cards (pass-2 chips blank for the same wrong-side reason).
      ...((head?.pass_no ?? 1) > 1
        ? { seps: [], artworkCount: 0, resolvedArtworks: 0 }
        : unionFamilySeps(arr.map((e) => e.customer_ref), resByRef)),
      setup: sum((e) => e.planned_setup_min || 0),
      run: sum((e) => e.planned_run_min || 0),
      washup: sum((e) => e.planned_washup_min || 0),
      earliestDue: dueOffset(head?.due_date, base),
      units: 4,
      parked,
      pass: head?.pass_no ?? 1,
      twoPass: (head?.pass_count ?? 1) > 1,
      familyRunId: head?.family_run_id ?? null,
    });
  });
  fams.sort((a, b) => (a.earliestDue ?? 9999) - (b.earliestDue ?? 9999));
  return fams;
}

export interface ScheduleFamilyVars {
  fam: GanttFamily;
  press: string;
  planned_start: string | null; // null => append at the press tail (next available)
  /**
   * When true the family is anchored at planned_start (sequence=1, existing
   * entries bumped behind it). Jobs are POSTed in REVERSE so the final lane
   * order matches the family's job order. Used by "specific time" scheduling;
   * ignored when planned_start is null (a pure append).
   */
  at_anchor?: boolean;
  /**
   * 1-based queue position to insert the family at (gap-opening drop). The
   * family's jobs go in IN ORDER at at_sequence, at_sequence+1, … so the card's
   * job order survives the insert. Takes precedence over at_anchor.
   */
  at_sequence?: number;
}

export function useGanttData() {
  const qc = useQueryClient();
  const base = useMemo(todayMidnight, []);
  const baseMs = base.getTime();
  const nowAbs = (Date.now() - baseMs) / MS_MIN;
  const colourMap = useCustomerColours();

  const pressConfigQ = useQuery({ queryKey: productionKeys.pressConfig(), queryFn: listPressConfig });
  const pressCodes = useMemo(
    () => (pressConfigQ.data ?? [])
      .map((p) => p.press_code)
      .sort((a, b) => pressRank(a) - pressRank(b) || a.localeCompare(b)),
    [pressConfigQ.data],
  );
  const fetchCodes = useMemo(() => [...pressCodes, PARK_CODE, ELSEWHERE_CODE], [pressCodes]);

  // One request for every press, fired at mount — it no longer waits on
  // press-config, which used to hold the four lane fetches back ~1 s.
  // Background-polled so live press actuals (progress, machine status,
  // "x ago" freshness) keep updating on a long-open board without a manual
  // refresh. Without this the query only refetches on focus/mount or a
  // mutation, so an idle board silently goes stale.
  const scheduleQ = useQuery({
    queryKey: productionKeys.scheduleAll(),
    queryFn: listScheduleAll,
    refetchInterval: LIVE_REFRESH_MS,
  });
  const scheduleUpdatedAt = scheduleQ.dataUpdatedAt;
  const rowsByPress = useMemo(() => {
    const m = new Map<string, ScheduleEntry[]>();
    (scheduleQ.data ?? []).forEach((e) => {
      const list = m.get(e.press_code);
      if (list) list.push(e); else m.set(e.press_code, [e]);
    });
    return m;
  }, [scheduleQ.data]);
  const rowsForCode = (code: string): ScheduleEntry[] => rowsByPress.get(code) ?? [];

  const downtimeQ = useQuery({
    queryKey: productionKeys.downtimeWindows(),
    queryFn: () => listDowntimeWindows(),
  });
  const unscheduledParams = useMemo(() => ({ unscheduled: true }), []);
  const unscheduledQ = useQuery({
    queryKey: productionKeys.jobs(unscheduledParams),
    queryFn: () => listJobs(unscheduledParams),
  });

  const lanes: GanttLane[] = useMemo(() => {
    const cfg = pressConfigQ.data ?? [];
    const byCode = new Map(cfg.map((c) => [c.press_code, c]));
    const press = pressCodes.map((code) => {
      const c = byCode.get(code);
      return {
        code, name: c?.notes || code, kind: 'press' as const,
        ink_units: c?.ink_units ?? 0, coater_units: c?.coater_units ?? 0,
      };
    });
    return [...press, { code: PARK_CODE, name: 'Parking', kind: 'park' as const, ink_units: 0, coater_units: 0 }];
  }, [pressConfigQ.data, pressCodes]);

  // Real artwork inks. Scheduled bars are FULL refs — bulk answer first, then
  // the one-at-a-time trickle against live Cloudflow for genuine cache misses.
  // The Ready-to-Run pool + held chips ride along as BULK-ONLY refs: they read
  // whatever the cache already holds (~87% of the pool, measured 2026-09-03)
  // through the same cache-only bulk request, but a miss never joins the
  // trickle — so showing colours on the backlog costs prod Cloudflow nothing.
  const allRefs = useMemo<ArtworkRef[]>(() => {
    const out: ArtworkRef[] = [];
    fetchCodes.forEach((code) => {
      const held = code === PARK_CODE || code === ELSEWHERE_CODE;
      rowsForCode(code).forEach((e) =>
        out.push({ jobNumber: e.job_number, customerRef: e.customer_ref, bulkOnly: held }));
    });
    (unscheduledQ.data ?? []).forEach((j) =>
      out.push({ jobNumber: j.job_number, customerRef: j.customer_ref, bulkOnly: true }));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleUpdatedAt, fetchCodes, unscheduledQ.dataUpdatedAt]);
  const sepByRef = useArtworkSeparations(allRefs);

  const windows = downtimeQ.data ?? [];
  const entries: GanttEntry[] = useMemo(() => {
    const out: GanttEntry[] = [];
    fetchCodes.forEach((code) => {
      if (code === PARK_CODE || code === ELSEWHERE_CODE) return; // held, not on a lane
      rowsForCode(code).forEach((e) => out.push(adaptEntry(e, base, nowAbs, colourMap, sepByRef, windows)));
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleUpdatedAt, fetchCodes, baseMs, colourMap, sepByRef, downtimeQ.dataUpdatedAt]);

  const parkedRows = useMemo(() => rowsForCode(PARK_CODE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleUpdatedAt]);
  const parkedFamilies = useMemo(() => buildHeldFamilies(parkedRows, base, 'park', true, sepByRef), [parkedRows, base, sepByRef]);
  const parkedCount = parkedRows.length;

  const elsewhereRows = useMemo(() => rowsForCode(ELSEWHERE_CODE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleUpdatedAt]);
  const elsewhereFamilies = useMemo(() => buildHeldFamilies(elsewhereRows, base, 'elsewhere', false, sepByRef), [elsewhereRows, base, sepByRef]);
  const elsewhereCount = elsewhereRows.length;

  // Canvas horizon (minutes from today 00:00): always covers all scheduled work
  // plus headroom, with a 14-day floor so there's room to plan ahead. Drives
  // both the timeline width and how far downtime is materialised.
  const windowMin = useMemo(() => {
    const latest = entries.reduce((m, e) => Math.max(m, e.absEnd), 0);
    const span = Math.max(MIN_HORIZON_MIN, latest + HORIZON_BUFFER_MIN);
    return Math.ceil(span / 1440) * 1440; // round up to a whole day
  }, [entries]);

  const downtime: DowntimeInterval[] = useMemo(() => {
    const windows = downtimeQ.data ?? [];
    const from = new Date(baseMs - MS_DAY);
    const to = new Date(baseMs + windowMin * MS_MIN + MS_DAY);
    return materialiseDowntimeWindows(windows, from, to).filter((d) => d.effect === 'closed');
  }, [downtimeQ.data, baseMs, windowMin]);

  const families: GanttFamily[] = useMemo(
    () => buildFamilies(unscheduledQ.data ?? [], base, sepByRef),
    [unscheduledQ.data, base, sepByRef],
  );

  const isLoading =
    pressConfigQ.isLoading || scheduleQ.isLoading || unscheduledQ.isLoading;

  // What a board mutation can actually change. Every mutation used to
  // invalidate productionKeys.all, which refetched press-config, colours,
  // downtime, sync-status and every job's separations on each drag — 21 saves
  // in one minute produced 146 GETs (App Insights, 2026-08-31). A schedule
  // change touches the schedule and the unscheduled backlog; a downtime change
  // touches downtime and the schedule that reflows around it. Nothing else.
  const invalidateSchedule = () => {
    qc.invalidateQueries({ queryKey: ['production', 'schedule'] });
    qc.invalidateQueries({ queryKey: ['production', 'jobs'] });
  };
  const invalidateDowntime = () => {
    qc.invalidateQueries({ queryKey: ['production', 'downtime-windows'] });
    qc.invalidateQueries({ queryKey: ['production', 'schedule'] });
  };
  const totalJobs = families.reduce((s, f) => s + f.jobs.length, 0);

  // Schedule a whole family onto a lane (Parking = hold). Optimistically removes
  // the family's jobs from the backlog; the new bars appear on schedule refetch.
  const scheduleFamily = useMutation({
    mutationFn: async (vars: ScheduleFamilyVars) => {
      const start = vars.planned_start ?? new Date().toISOString();
      // Anchor only makes sense with an explicit start; a null start is a tail
      // append. When anchoring, POST in reverse so the LAST job lands at
      // sequence=1 and the rest fall in behind it (final order = job order).
      const atAnchor = !!vars.at_anchor && vars.planned_start != null;
      const jobs = atAnchor ? [...vars.fam.jobs].reverse() : vars.fam.jobs;
      // The family's run id (migration 141). A held chip already has one; a
      // backlog card gets one minted by the FIRST job's POST when it lands on
      // a press lane. Either way the id is threaded through the rest of the
      // loop so every row of the family joins the same run.
      let familyRunId: number | null = vars.fam.familyRunId ?? null;
      for (let i = 0; i < jobs.length; i += 1) {
        const j = jobs[i];
        // A held family (Parking / Scheduled-elsewhere) already owns schedule
        // rows — UNIQUE (job_number, pass_no). The POST handles that itself:
        // it MOVES the existing row onto the target press, re-resolving
        // setup/run/washup against the target press's standard. The row keeps
        // its identity, so its status and any press actuals bound while the
        // job was held survive the drop. (The old delete-then-create here
        // cascade-deleted recorded actuals when a job ran while parked —
        // 2026-09-01, jobs 283936/283937.)
        // eslint-disable-next-line no-await-in-loop
        const entry = await addScheduleEntry({
          job_number: j.job, press_code: vars.press, planned_start: start,
          // Which pass this card is placing. The card carries it, so scheduling
          // the Pass 2 card creates pass-2 rows and leaves pass 1 alone.
          pass_no: j.pass ?? vars.fam.pass ?? 1,
          ...(familyRunId != null ? { family_run_id: familyRunId } : {}),
          // Walk the slot forward per job so a 3-job family lands as a
          // contiguous run at N, N+1, N+2 rather than all fighting for N.
          ...(vars.at_sequence != null ? { at_sequence: vars.at_sequence + i } : {}),
          ...(atAnchor && vars.at_sequence == null ? { at_anchor: true } : {}),
        });
        if (familyRunId == null && entry?.family_run_id != null) familyRunId = entry.family_run_id;
      }
    },
    onMutate: async (vars) => {
      const key = productionKeys.jobs(unscheduledParams);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ProductionJob[]>(key);
      // Keyed by job AND pass: scheduling the Pass 1 card must not optimistically
      // rip the job's still-unscheduled Pass 2 row out of the dock.
      const famJobs = new Set(vars.fam.jobs.map((j) => `${j.job}|${j.pass ?? 1}`));
      qc.setQueryData<ProductionJob[]>(key, (old) =>
        (old ?? []).filter((j) => !famJobs.has(`${j.job_number}|${j.pass_no ?? 1}`)));
      return { prev, key };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Non-destructive edit of an already-scheduled bar: reorder, cross-press
  // move, resize, status/flag toggles — all via PATCH, so sequence, notes,
  // the actuals link, and the downtime flags survive the change. The server
  // re-cascades affected presses, so we just invalidate and re-fetch.
  const patchEntry = useMutation({
    mutationFn: (vars: { id: number; patch: ScheduleUpdate }) =>
      updateScheduleEntry(vars.id, vars.patch),
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Remove a scheduled/parked bar entirely (returns the job to Ready-to-Run).
  const removeEntry = useMutation({
    mutationFn: (vars: { id: number }) => deleteScheduleEntry(vars.id),
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Remove a whole group of bars in one action (the sibling run a dock card was
  // scheduled as). Deleted one at a time because each DELETE renumbers and
  // re-cascades its press server-side; a single invalidate at the end keeps the
  // board from reflowing once per row.
  const removeEntries = useMutation({
    mutationFn: async (vars: { ids: number[] }) => {
      for (const id of vars.ids) {
        // eslint-disable-next-line no-await-in-loop
        await deleteScheduleEntry(id);
      }
    },
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Raw single-entry add — used by undo to re-create a removed bar.
  const addEntry = useMutation({
    mutationFn: (vars: ScheduleCreate) => addScheduleEntry(vars),
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Downtime blocks & recurring closures. The backend re-cascades the press on
  // create/update/delete, so jobs reflow around the change automatically; we
  // just invalidate to pick up the new times. createBlock adds one-off blocks
  // from the toolbar; updateBlock edits a recurring closure's days/times (e.g.
  // the weekend band as rostering changes); deleteBlock removes either kind.
  const createBlock = useMutation({
    mutationFn: (vars: DowntimeWindowUpsert) => createDowntimeWindow(vars),
    onSettled: () => {
      invalidateDowntime();
    },
  });
  const updateBlock = useMutation({
    mutationFn: (vars: { id: number; patch: Partial<DowntimeWindowUpsert> }) =>
      updateDowntimeWindow(vars.id, vars.patch),
    onSettled: () => {
      invalidateDowntime();
    },
  });
  const deleteBlock = useMutation({
    mutationFn: (vars: { id: number }) => deleteDowntimeWindow(vars.id),
    onSettled: () => {
      invalidateDowntime();
    },
  });

  // Return a held family (Scheduled-elsewhere or Parking) to the Ready-to-Run
  // backlog by deleting its source schedule rows — the job has no schedule
  // entry again, so the unscheduled query picks it back up.
  const returnToBacklog = useMutation({
    mutationFn: async (vars: { fam: GanttFamily }) => {
      for (const j of vars.fam.jobs) {
        if (j.scheduleId != null) {
          // eslint-disable-next-line no-await-in-loop
          await deleteScheduleEntry(j.scheduleId);
        }
      }
    },
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Manual regrouping: split a subset of a family's jobs into a new card
  // (server assigns a split_key) or rejoin a split card to its default forme
  // group. On a SCHEDULED family the same calls are the split / merge events
  // for its family run (a second run for the subset; merged back on rejoin).
  // Grouping is recomputed from the refetched jobs and schedule.
  const splitFamily = useMutation({
    mutationFn: (vars: { jobNumbers: string[] }) => splitJobs(vars.jobNumbers),
    onSettled: () => {
      invalidateSchedule();
    },
  });
  const rejoinFamily = useMutation({
    mutationFn: (vars: { splitKey: string } | { jobNumbers: string[] }) =>
      ('splitKey' in vars ? rejoinSplit(vars.splitKey) : rejoinJobs(vars.jobNumbers)),
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // "Rebase to reality": commit the live projection into planned_start for a
  // press's not-started entries. The server moves only planned rows; we
  // invalidate so the ghost projection collapses onto the committed bars.
  const rebasePress = useMutation({
    mutationFn: (vars: { press: string }) => rebaseSchedule(vars.press),
    onSettled: () => {
      invalidateSchedule();
    },
  });

  // Admin / testing clean-up: wipe all not-started jobs on a press. The
  // cleared jobs lose their schedule row and reappear in Ready-to-Run.
  const clearFuturePress = useMutation({
    mutationFn: (vars: { press: string }) => clearFutureSchedule(vars.press),
    onSettled: () => {
      invalidateSchedule();
    },
  });

  return {
    base, baseMs, nowAbs, windowMin,
    lanes, entries, parkedCount, parkedFamilies, elsewhereFamilies, elsewhereCount,
    // `downtime` is materialised+filtered for painting; `downtimeWindows` is the
    // raw rule set, which the insertion preview needs to walk a run around a
    // closure the same way adaptEntry does.
    downtime, downtimeWindows: windows, families, totalJobs,
    isLoading, scheduleFamily, patchEntry, removeEntry, removeEntries, addEntry,
    createBlock, updateBlock, deleteBlock, returnToBacklog,
    splitFamily, rejoinFamily, rebasePress, clearFuturePress,
  };
}
